import { QuillBinding } from "y-quill"
import { SSEProvider } from "./yjs-utils/sse-provider"
import { TemplateDoc } from "./yjs-utils/template-doc"
import Quill, { QuillOptions } from "quill"
import { Accessor, createEffect, createSignal, Setter } from "solid-js"
import { collaborativeEdit as api, CursorInfo } from 'st-app/api'
import * as Y from 'yjs'
import QuillCursors from "quill-cursors"

export type EditSource = 'user' | 'collaborator'

export interface Collaborator {
    id: number 
    name: string
    color: string
    initials: string
    online: boolean
    active: boolean
}

export interface CollaborationSession {
    key: string
    ydoc: TemplateDoc
    provider: SSEProvider
    editor: Quill
    binding: QuillBinding
}

// We don't need to re-load the cursor info for every new textbox, so we can just keep it in memory
let cachedCursorInfo: CursorInfo | null = null

export type ValueSetHandler = (value: string, source: EditSource) => void
export type PropertySavedHandler = (value: string) => void

export class CollaborativeEditContext {
    private id: number = 0;

    session: CollaborationSession | null = null;
    collaborators: Accessor<Collaborator[]>; setCollaborators: Setter<Collaborator[]>;
    onPropertySaved: PropertySavedHandler | undefined;
    onValueSet: ValueSetHandler | undefined;
    isRichText: boolean = false;
    isFocused: Accessor<boolean>; setIsFocused: Setter<boolean>;
    isSaving: Accessor<boolean>; setIsSaving: Setter<boolean>;
    isFullscreen: Accessor<boolean>; setIsFullscreen: Setter<boolean>;
    value: Accessor<string>; setValue: Setter<string>;
    cloudSyncTimer: number | undefined = undefined;
    srcName: Accessor<string>;
    identity: Accessor<string>;

    constructor(srcName: Accessor<string>, identity: Accessor<string>, isRichText: boolean, onValueSet?: ValueSetHandler, onPropertySaved?: PropertySavedHandler) {
        [this.collaborators, this.setCollaborators] = createSignal<Collaborator[]>([]);
        [this.isFocused, this.setIsFocused] = createSignal<boolean>(false);
        [this.isFullscreen, this.setIsFullscreen] = createSignal<boolean>(false);
        [this.value, this.setValue] = createSignal<string>('');
        [this.isSaving, this.setIsSaving] = createSignal<boolean>(false);
        this.srcName = srcName;
        this.identity = identity;
        this.onPropertySaved = onPropertySaved;
        this.onValueSet = onValueSet;
        this.isRichText = isRichText;

        this.id = Math.floor(Math.random() * 1000000)
    }

    initQuill(container: HTMLDivElement): Quill {
        const el = document.createElement('div')
        container.replaceChildren(el)
        if (this.cloudSyncTimer) {
            clearTimeout(this.cloudSyncTimer)
            this.cloudSyncTimer = undefined;
        }

        const quillOptions: QuillOptions = {
            modules: {
                cursors: true,
                toolbar: false,
                history: {
                    userOnly: true
                }
            },
            formats: [],
            theme: 'snow',
        }

        if (this.isRichText) {
            quillOptions.modules!['toolbar'] = [
                [{ header: [1, 2, false] }],
                ['bold', 'italic', 'underline'],
                ['image', 'code-block'],
            ]
            quillOptions.formats = ['bold', 'italic', 'underline', 'header', 'image', 'code-block']
        }

        const editor = new Quill(el, quillOptions)

        editor.on('text-change', (delta, oldContents, source) => {
            const value = this.isRichText ? editor.getSemanticHTML() : editor.getText() ?? ''
            
            const trimmedValue = value.endsWith('\n') ? value.slice(0, -1) : value
            this.setValue(trimmedValue)
            this.onValueSet?.(trimmedValue, source === 'user' ? 'user' : 'collaborator')

            if (source === 'user') {
                if (this.cloudSyncTimer) {
                    clearTimeout(this.cloudSyncTimer)
                }

                this.cloudSyncTimer = window.setTimeout(() => {
                    if (this.isFocused()) {
                        this.save();
                    }
                }, 2000)
            }
        })

        editor.on('selection-change', (range, oldRange, source) => {
            this.setIsFocused(!!range)
            // this.setIsFocused(true)
        })

        return editor
    }

    get cursorInfo() {
        return cachedCursorInfo!
    }

    startSession(container: HTMLDivElement, key: string, value: string) {
        const previousKey = this.session?.key

        this.stopSession()

        const editor = this.initQuill(container)
        const ydoc = new TemplateDoc({ initialValue: value });
        const provider = new SSEProvider(key, ydoc, { onPropertySaved: this.onPropertySaved, id: this.id })
        const binding = new QuillBinding(ydoc.getText('quill'), editor, provider.awareness);
        this.session = { key, ydoc, provider, binding, editor }

        if (cachedCursorInfo) {
            provider.updatePresence(cachedCursorInfo)
        } else {
            api.GetCursorInfo().then(cursor => {
                cachedCursorInfo = cursor
                provider.updatePresence(cursor)
            })
        }

        provider.awareness.on('change', () => {
            const states = provider?.awareness.getStates()
            if (!states) return
    
            const c = [...states.entries()].map(([id, state]) => this.buildCollaborator(id, state)).filter(state => state.active && state.online && state.id !== provider?.awareness.clientID)

            this.setCollaborators(c as any)
        })

        Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(ydoc))

        if (previousKey && previousKey !== key) {
            api.LeaveCollaboration(previousKey)
        }

        api.JoinCollaboration(key)

    }

    stopSession(): string | undefined {
        if (!this.session)
            return undefined

        this.session?.provider?.destroy();
        this.session?.ydoc?.destroy();
        this.session?.binding?.destroy();
        this.session = null
    }

    buildCollaborator(id: number, state: { [x: string]: any }): Collaborator {
        const online = !!(state?.user)
        const name = state?.user?.name ?? 'Unknown'
        const color = state?.user?.color ?? '#000000'
        const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
        const active = !!state?.cursor;
        return { id, name, color, initials, online, active }
    }

    toggleFullscreen() {
        this.setIsFullscreen(!this.isFullscreen())
    }

    async save() {
        this.setIsSaving(true)
        try {
            const minDelay = new Promise((resolve) => setTimeout(resolve, 1000))
            const savePromise = api.SaveValue(this.srcName(), this.identity(), this.value())
            await Promise.all([savePromise, minDelay])
        } catch (e) {
            console.error('Error saving value', e)
        }
        this.setIsSaving(false)
    }
}


const colorFromName = (name: string) => {
    const hash = hashString(name)
    const { h, s, l } = hslFromHash(hash)
    return hslToRgb(h, s, l)
}

const hashString = (str: string) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    }
    return hash >>> 0;
}

const hslFromHash = (hash: number) => {
    const h = hash % 360;
    const s = (hash >>> 8) % 21 + 65;
    const l = (hash >>> 16) % 11 + 35;
    return { h, s, l };
}

const hslToRgb = (h: number, s: number, l: number) => {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h/30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return {
        r: Math.round(255 * f(0)),
        g: Math.round(255 * f(8)),
        b: Math.round(255 * f(4)),
    };
}
